import type { BookingCardProps } from '@/lib/render'
import { Card } from '@/components/ui/card'

interface Props {
  props: unknown
}

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  booked:       { bg: '#eff6ff', text: '#1d4ed8', label: 'Booked' },
  completed:    { bg: '#f0fdf4', text: '#15803d', label: 'Completed' },
  cancelled:    { bg: '#fef2f2', text: '#b91c1c', label: 'Cancelled' },
  did_not_show: { bg: '#fff7ed', text: '#c2410c', label: 'Did Not Show' },
  rescheduled:  { bg: '#faf5ff', text: '#7e22ce', label: 'Rescheduled' },
}

export function BookingCardRenderer({ props }: Props) {
  const data = props as BookingCardProps

  if (!data?.booking) {
    return (
      <div className="rounded-lg bg-destructive/10 p-4">
        <p className="text-[13px] text-destructive">Invalid booking data</p>
      </div>
    )
  }

  const b = data.booking
  const statusStyle = STATUS_COLORS[b.status] ?? { bg: '#f8fafc', text: '#475569', label: b.status }

  const start = new Date(b.start)
  const end = new Date(b.end)
  const duration = Math.round((end.getTime() - start.getTime()) / 60000)

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="px-4 py-2" style={{ backgroundColor: statusStyle.bg }}>
        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: statusStyle.text }}>
          {statusStyle.label}
        </p>
      </div>

      <div className="flex flex-col gap-2 p-4">
        <p className="mb-0.5 text-[17px] font-bold text-foreground">{b.service_name}</p>

        {b.customer_name ? (
          <p className="mb-2 text-sm text-muted-foreground">{b.customer_name}</p>
        ) : null}

        <div className="flex gap-3">
          <span className="w-12 pt-px text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</span>
          <span className="min-w-0 flex-1 text-[13px] text-foreground">
            {start.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>

        <div className="flex gap-3">
          <span className="w-12 pt-px text-xs font-semibold uppercase tracking-wide text-muted-foreground">Time</span>
          <span className="min-w-0 flex-1 text-[13px] text-foreground">
            {start.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            {' – '}
            {end.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            {' '}
            ({duration} min)
          </span>
        </div>

        <div className="flex gap-3">
          <span className="w-12 pt-px text-xs font-semibold uppercase tracking-wide text-muted-foreground">Price</span>
          <span className="min-w-0 flex-1 text-[13px] text-foreground">
            {b.currency} {Number(b.price).toFixed(2)}
            {b.balance_due !== undefined && b.balance_due > 0 ? (
              <span className="whitespace-pre-wrap font-semibold text-destructive">
                {'  '}Balance due: {b.currency} {Number(b.balance_due).toFixed(2)}
              </span>
            ) : null}
          </span>
        </div>
      </div>
    </Card>
  )
}
